import { SaveRecordAction } from '@aibees/elephant/hooks';
import { SearchTable } from '@aibees/elephant/types';
import { FormDialogConfig } from '@aibees/shared/components';
import { CategoryKey, OptionType } from '@aibees/shared/features';
import * as Yup from 'yup';
import { dialogIcon } from '../save-dialog/sx';
import { SearchTableActionDialogNotesConfig } from '../search-table-action-dialog-notes';

export const scheme = Yup.object().shape({
  name: Yup.string()
    .required('Name is required')
    .max(100, 'Name must be at most 100 characters'),
  owner: Yup.string(),
  overwriteOwner: Yup.boolean(),
  selectOwner: Yup.boolean(),
  [SaveRecordAction.CONTACT_ENRICH_EMAIL]: Yup.boolean(),
  [SaveRecordAction.CONTACT_REVERIFY_EMAIL]: Yup.boolean(),
  [SaveRecordAction.BASIC_SAVE]: Yup.boolean(),
  [SaveRecordAction.CONTACT_REVEAL_ANALYSIS]: Yup.boolean(),
  [SaveRecordAction.CONTACT_REVEAL_PROFILE]: Yup.boolean(),
  // [SaveRecordAction.AI_POWERED_ACCOUNT]: Yup.boolean(),
  // [SaveRecordAction.AI_POWERED_CONTACT]: Yup.boolean(),
});

export const config: FormDialogConfig = {
  icon: 'file-download-outline',
  iconSx: dialogIcon,
  submitButtonProps: {
    children: 'Export',
  },
  closeButton: {
    children: 'Cancel',
  },

  scheme,
  defaultValues: {
    selectOwner: true,
    [SaveRecordAction.CONTACT_ENRICH_EMAIL]: true,
    [SaveRecordAction.CONTACT_REVERIFY_EMAIL]: true,
    [SaveRecordAction.CONTACT_REVEAL_ANALYSIS]: false,
    [SaveRecordAction.CONTACT_REVEAL_PROFILE]: false,
    [SaveRecordAction.BASIC_SAVE]: true,
    [SaveRecordAction.AI_POWERED_ACCOUNT]: true,
    [SaveRecordAction.AI_POWERED_CONTACT]: true,
  },
};

export const noteConfig: Partial<
  Record<SearchTable, SearchTableActionDialogNotesConfig[]>
> = {
  [SearchTable.People]: [
    {
      title:
        'If you choose "Profile Insight" for your export, customize your template. By default, only a few data points are included. To access all 131 data points, go to "Edit Templates" and select the relevant fields.',
    },
  ],
  [SearchTable.Employees]: [
    {
      title:
        'No charges for already enriched/saved data. Our system detects previously enriched/saved info, ensuring you only pay for new services.',
    },
  ],
  [SearchTable.BuyingTeam]: [
    {
      title: '',
    },
  ],
};

export const noteConfigLookalike: Partial<
  Record<SearchTable, SearchTableActionDialogNotesConfig[]>
> = {
  [SearchTable.People]: [
    {
      title:
        'If you choose "Profile Insight" for your export, customize your template. By default, only a few data points are included. To access all 131 data points, go to "Edit Templates" and select the relevant fields.',
    },
  ],
  [SearchTable.Employees]: [
    {
      title:
        'No charges for already enriched/saved data. Our system detects previously enriched/saved info, ensuring you only pay for new services.',
    },
  ],
  [SearchTable.BuyingTeam]: [
    {
      title: '',
    },
  ],
};

export const exportTEmplateNoteConfig: Partial<
  Record<SearchTable, SearchTableActionDialogNotesConfig[]>
> = {
  [SearchTable.People]: [
    {
      title:
        'If you choose "Profile Insight" for your export, customize your template. By default, only a few data points are included. To access all 131 data points, go to "Edit Templates" and select the relevant fields.',
    },
  ],
  [SearchTable.BuyingTeam]: [
    {
      title: '',
    },
  ],
  [SearchTable.Employees]: [
    {
      title: '',
    },
  ],
};

export const defaultPeopleExportSetting = {
  default: true,
  items: [
    {
      key: CategoryKey.basic,
      categoryName: 'people basic',
      isSingleEntry: true,
      entries: [
        {
          index: 0,
          fields: [
            OptionType.FIRSTNAME,
            OptionType.LASTNAME,
            OptionType.TITLE,
            OptionType.BUSINESS_STATUS,
            OptionType.DOMAIN_SETTING,
            OptionType.MX_RECORD,
            OptionType.EMAIL_BUSINESS,
            OptionType.LOCATION,
            OptionType.SENIORITY,
            OptionType.DEPARTMENT,
            OptionType.LINKEDIN,
          ],
        },
      ],
    },
    {
      key: CategoryKey.experiences,
      categoryName: 'experience',
      entries: [
        {
          index: 0,
          fields: [
            OptionType.TITLE,
            OptionType.COMPANY_URL,
            OptionType.SUMMARY,
            OptionType.COMPANY,
            OptionType.IS_CURRENT,
          ],
        },
        { index: -1, isBottom: true, fields: [] },
      ],
    },
    {
      key: CategoryKey.educations,
      categoryName: 'education',
      entries: [
        { index: 0, fields: [OptionType.SCHOOL] },
        { index: -1, isBottom: true, fields: [] },
      ],
    },
    {
      key: CategoryKey.awards,
      categoryName: 'awards',
      entries: [
        { index: 0, fields: [OptionType.TITLE] },
        { index: -1, isBottom: true, fields: [] },
      ],
    },
    {
      key: CategoryKey.courses,
      categoryName: 'courses',
      entries: [
        { index: 0, fields: [OptionType.TITLE] },
        { index: -1, isBottom: true, fields: [] },
      ],
    },
    {
      key: CategoryKey.projects,
      categoryName: 'projects',
      entries: [
        { index: 0, fields: [OptionType.TITLE] },
        { index: -1, isBottom: true, fields: [] },
      ],
    },
    {
      key: CategoryKey.languages,
      categoryName: 'languages',
      entries: [
        { index: 0, fields: [OptionType.LANGUAGE] },
        { index: 1, fields: [OptionType.LANGUAGE] },
        { index: 2, fields: [OptionType.LANGUAGE] },
        { index: -1, isBottom: true, fields: [] },
      ],
    },
    {
      key: CategoryKey.certifications,
      categoryName: 'certification',
      entries: [
        { index: 0, fields: [OptionType.TITLE] },
        { index: -1, isBottom: true, fields: [] },
      ],
    },
    {
      key: CategoryKey.volunteerExperiences,
      categoryName: 'volunteering',
      entries: [
        {
          index: 0,
          fields: [
            OptionType.SUMMARY,
            OptionType.ROLE,
            OptionType.COMPANY_NAME,
          ],
        },
        { index: -1, isBottom: true, fields: [] },
      ],
    },
    {
      key: CategoryKey.publications,
      categoryName: 'publications',
      entries: [
        { index: 0, fields: [OptionType.TITLE] },
        { index: -1, isBottom: true, fields: [] },
      ],
    },
    {
      key: CategoryKey.patents,
      categoryName: 'patent',
      disabled: true,
      entries: [
        { index: 0, fields: [OptionType.TITLE] },
        { index: -1, isBottom: true, fields: [] },
      ],
    },
    {
      key: CategoryKey.company,
      categoryName: 'first current company basic',
      isSingleEntry: true,
      entries: [
        {
          index: 0,
          fields: [
            OptionType.NAME,
            OptionType.EMPLOYEE_COUNT,
            OptionType.INDUSTRY,
            OptionType.PRODUCT_AND_SERVICES,
            OptionType.SEO_DESCRIPTION,
            OptionType.WEBSITE,
            OptionType.LINKEDIN,
            OptionType.TWITTER,
            OptionType.FACEBOOK,
            OptionType.INSTAGRAM,
            OptionType.TYPE,
            OptionType.LOCATION,
            OptionType.FOUNDING_YEAR,
            OptionType.ANNUAL_REVENUE,
            OptionType.TOTAL_FUNDING,
            OptionType.LAST_FUNDING_TYPE,
            OptionType.LAST_FUNDING_AMOUNT,
            OptionType.LAST_FUNDING_DATE,
          ],
        },
      ],
    },
    {
      key: CategoryKey.companyLocations,
      categoryName: 'first current company location',
      entries: [
        { index: 0, fields: [OptionType.ADDRESS, OptionType.PRIMARY] },
        { index: -1, isBottom: true, fields: [] },
      ],
    },
  ],
};

export const defaultCompanyExportSetting = {
  default: true,
  items: [
    {
      key: CategoryKey.company,
      categoryName: 'company',
      isSingleEntry: true,
      entries: [
        {
          index: 0,
          fields: [
            OptionType.ANNUAL_REVENUE,
            OptionType.CITY,
            OptionType.COUNTRY,
            OptionType.DESCRIPTION,
            OptionType.DOMAIN,
            OptionType.ECONOMIC_ZONE,
            OptionType.EMPLOYEE_COUNT,
            OptionType.FACEBOOK,
            OptionType.FOLLOWER_COUNT,
            OptionType.FOUNDING_YEAR,
            OptionType.ID,
            OptionType.INDUSTRY,
            OptionType.INSTAGRAM,
            OptionType.LABELS,
            OptionType.LAST_FUNDING_AMOUNT,
            OptionType.LAST_FUNDING_DATE,
            OptionType.LAST_FUNDING_TYPE,
            OptionType.LINKEDIN,
            OptionType.LISTS,
            OptionType.LOCATION,
            OptionType.LOGO,
            OptionType.NAME,
            OptionType.NUMBER_OF_LOCATION,
            OptionType.OP_LANGUAGE,
            OptionType.ORG_ID,
            OptionType.OWNER,
            OptionType.POSTAL_CODE,
            OptionType.PRIMARY_PHONE,
            OptionType.PRODUCT_AND_SERVICES,
            OptionType.SECONDARY_PHONE,
            OptionType.SEO_DESCRIPTION,
            OptionType.SIZE,
            OptionType.SLUG,
            OptionType.STATE,
            OptionType.TECHNOLOGIES,
            OptionType.TOTAL_FUNDING,
            OptionType.TWITTER,
            OptionType.TYPE,
            OptionType.WEBSITE,
          ],
        },
      ],
    },
  ],
};
